import TomSelect from 'tom-select/dist/js/tom-select.base.js'
import { createPopper } from '@popperjs/core/lib/popper-lite'
import flip from '@popperjs/core/lib/modifiers/flip'
import preventOverflow from '@popperjs/core/lib/modifiers/preventOverflow'
import { isNativeApp } from '../util/detection'

type TomSelectInstance = InstanceType<typeof TomSelect>
type TomSelectOptions = ConstructorParameters<typeof TomSelect>[1]
type TomSelectWithPopper = TomSelectInstance & { popper: ReturnType<typeof createPopper> }

up.compiler(
  '[data-tom-select]',
  function (element: HTMLSelectElement, data: { countries?: string[] }) {
    element.classList.remove('form-control') // class prevents dropdown arrows
    if (element.matches('[data-place-select]')) {
      placeSelect()
    } else if (element.matches('[data-remote-select]')) {
      remoteSelect()
    } else {
      basicSelect()
    }

    function renderNoResults() {
      return '<div class="no-results">Keine Übereinstimmungen gefunden</div>'
    }

    function enableWhiteout() {
      document.body.classList.add('-whiteout-visible')
    }

    function disableWhiteout() {
      document.body.classList.remove('-whiteout-visible')
    }

    function handleDropdownOpenOnNative(dropdown: HTMLDivElement) {
      if (isNativeApp()) {
        const container = dropdown.parentElement!
        container.classList.add('-tom-select-fixed')
        enableWhiteout()
      }
    }

    function handleDropdownCloseOnNative(dropdown: HTMLDivElement) {
      if (isNativeApp()) {
        const container = dropdown.parentElement!
        container.classList.remove('-tom-select-fixed')
        disableWhiteout()
      }
    }

    function dropdownParent() {
      if (isNativeApp()) {
        return null // Native app has fixed modals that need to be attached to the container
      } else {
        return element.closest('up-modal') || null
      }
    }

    function basicSelect() {
      return buildTomSelect({
        render: {
          no_results: renderNoResults,
        },
      })
    }

    function placeSelectOptgroupsNoResults() {
      return '<div class="no-results">Das ist keine gültige Postleitzahl</div>'
    }

    function placeSelectOptgroups() {
      const optgroups: { value: string; label: string }[] = []
      const countries = data.countries
      if (countries !== undefined) {
        countries.forEach((country) => {
          optgroups.push({ value: country, label: country })
        })
      }
      return optgroups
    }

    function placeSelect() {
      return buildTomSelect({
        valueField: 'id',
        labelField: 'text',
        searchField: 'text',
        optgroupField: 'group',
        optgroups: placeSelectOptgroups(),
        lockOptgroupOrder: true,
        render: {
          no_results: placeSelectOptgroupsNoResults,
        },
        shouldLoad: function (query: string) {
          return query.length >= 4 // minimum input length to make the request
        },
        load: loadResults,
      })
    }

    function remoteSelectNoResults() {
      return '<div class="no-results">Zu dieser Eingabe wurden leider keine Ergebnisse gefunden.</div>'
    }

    function remoteSelect() {
      return buildTomSelect({
        valueField: 'id',
        labelField: 'text',
        searchField: 'text',
        render: {
          no_results: remoteSelectNoResults,
        },
        load: loadResults,
      })
    }

    function loadResults(query: string, callback: (results?: object) => void) {
      const url = element.getAttribute('url') + '?term=' + encodeURIComponent(query)
      fetch(url)
        .then((response) => response.json())
        .then((json) => {
          callback(json)
        })
        .catch(() => {
          callback()
        })
    }

    function buildTomSelect(options: TomSelectOptions) {
      const tomSelect = new TomSelect(element, {
        ...options,
        create: false,
        dropdownParent: dropdownParent(),
        closeAfterSelect: true,
        sortField: [{ field: '$order' }, { field: '$score' }], // sort first by default order and then by relevance score
        onInitialize: function (this: TomSelectWithPopper) {
          this.dropdown.style.margin = '0' // needed to remove popper.js warnings due to margin in the browser console
          this.popper = createPopper(this.control, this.dropdown, {
            modifiers: [flip, preventOverflow],
          })
        },
        onDropdownOpen: function (dropdown: HTMLDivElement) {
          handleDropdownOpenOnNative(dropdown)
        },
        onDropdownClose: function (dropdown: HTMLDivElement) {
          handleDropdownCloseOnNative(dropdown)
        },
      })

      tomSelect.positionDropdown = function (this: TomSelectWithPopper) {
        void this.popper.update()
        this.dropdown.style.width = `${this.control.getBoundingClientRect().width}px`
      }

      tomSelect.on('destroy', function (this: TomSelectWithPopper) {
        this.popper.destroy()
      })

      return tomSelect
    }

    return () => {
      ;(element as HTMLSelectElement & { tomselect: TomSelectInstance }).tomselect.destroy()
    }
  },
)
